import React, { useEffect, useState, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import {
    ArrowLeft, User, Clock, DollarSign, CreditCard,
    TrendingUp, TrendingDown, Minus, ShoppingBag,
    XCircle, CheckCircle, MapPin, Package, FileText,
    Ticket, ChevronLeft, ChevronRight
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useUser } from 'context/user/User';
import cashierSessionsApi from 'api/requests/cashierSessions';
import colors from 'theme/colors';
import currency from 'helpers/currency';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';

dayjs.locale('pt-br');

function fmt(v) { return currency.format(Number(v) || 0); }
function fmtDate(d) { return dayjs(d).format('DD/MM/YYYY'); }
function fmtTime(d) { return dayjs(d).format('HH:mm'); }
function fmtDateTime(d) { return dayjs(d).format('DD/MM/YYYY [às] HH:mm'); }

const METHOD_STYLES = {
    'Dinheiro':           { color: '#2BC866', bg: '#2BC86612' },
    'Cartão de Crédito':  { color: '#3F6BC6', bg: '#3F6BC612' },
    'Cartão de Débito':   { color: '#5B8FE8', bg: '#5B8FE812' },
    'Pix':                { color: '#8025e8', bg: '#8025e812' },
};
function getMethodStyle(name) {
    return METHOD_STYLES[name] || { color: colors.night, bg: `${colors.night}12` };
}

export default function CashierSessionPage() {
    const { user } = useUser();
    const { sessionId } = useParams();
    const navigate = useNavigate();
    const slug = user?.establishments?.[0]?.slug;

    const [loading, setLoading] = useState(true);
    const [session, setSession] = useState(null);
    const [summary, setSummary] = useState(null);
    const [error, setError] = useState('');

    const emptyPagination = { expanded: false, loading: false, data: [], page: 1, lastPage: 1, total: 0 };
    const [productsPagination, setProductsPagination] = useState(emptyPagination);
    const [ticketsPagination, setTicketsPagination] = useState(emptyPagination);

    const fetchSession = useCallback(async () => {
        if (!slug || !sessionId) return;
        setLoading(true);
        setError('');
        try {
            const [sessionRes, summaryRes] = await Promise.all([
                cashierSessionsApi.show(slug, sessionId),
                cashierSessionsApi.summary(slug, sessionId),
            ]);
            setSession(sessionRes.data.data || sessionRes.data);
            setSummary(summaryRes.data.data || summaryRes.data);
        } catch {
            setError('Não foi possível carregar o relatório deste caixa.');
        } finally {
            setLoading(false);
        }
    }, [slug, sessionId]);

    const fetchProductsSold = useCallback(async (page = 1) => {
        setProductsPagination(p => ({ ...p, loading: true }));
        try {
            const res = await cashierSessionsApi.productsSold(slug, sessionId, { page, per_page: 15 });
            const d = res.data;
            setProductsPagination({ expanded: true, loading: false, data: d.data, page: d.current_page, lastPage: d.last_page, total: d.total });
        } catch {
            setProductsPagination(p => ({ ...p, loading: false }));
        }
    }, [slug, sessionId]);

    const fetchTicketsSold = useCallback(async (page = 1) => {
        setTicketsPagination(p => ({ ...p, loading: true }));
        try {
            const res = await cashierSessionsApi.ticketsSold(slug, sessionId, { page, per_page: 15 });
            const d = res.data;
            setTicketsPagination({ expanded: true, loading: false, data: d.data, page: d.current_page, lastPage: d.last_page, total: d.total });
        } catch {
            setTicketsPagination(p => ({ ...p, loading: false }));
        }
    }, [slug, sessionId]);

    useEffect(() => { fetchSession(); }, [fetchSession]);

    if (loading) {
        return (
            <Page>
                <LoadingWrap>
                    <Spinner />
                    <LoadingLabel>Carregando relatório...</LoadingLabel>
                </LoadingWrap>
            </Page>
        );
    }

    if (error || !session) {
        return (
            <Page>
                <BackBtn onClick={() => navigate(-1)}>
                    <ArrowLeft size={16} />
                    Voltar
                </BackBtn>
                <ErrorCard>{error || 'Sessão não encontrada.'}</ErrorCard>
            </Page>
        );
    }

    const diff = (Number(session.closing_balance) || 0) - (Number(session.expected_balance) || 0);

    return (
        <Page>
            {/* ─── Header ─── */}
            <Header>
                <BackBtn onClick={() => navigate(-1)}>
                    <ArrowLeft size={16} />
                    Voltar
                </BackBtn>
                <HeaderMeta>
                    <HeaderDate>{fmtDate(session.opened_at)}</HeaderDate>
                    <HeaderTimeRange>
                        {fmtTime(session.opened_at)} — {fmtTime(session.closed_at)}
                    </HeaderTimeRange>
                </HeaderMeta>
            </Header>

            {/* ─── Session info card ─── */}
            <InfoCard>
                <InfoRow>
                    <InfoIcon><User size={15} /></InfoIcon>
                    <InfoLabel>Aberto por</InfoLabel>
                    <InfoValue>{session.opened_by?.name ?? '—'}</InfoValue>
                </InfoRow>
                {session.closed_by && (
                    <InfoRow>
                        <InfoIcon><User size={15} /></InfoIcon>
                        <InfoLabel>Fechado por</InfoLabel>
                        <InfoValue>{session.closed_by.name} · {fmtDateTime(session.closed_at)}</InfoValue>
                    </InfoRow>
                )}
                {session.pos_event?.name && (
                    <InfoRow>
                        <InfoIcon><Clock size={15} /></InfoIcon>
                        <InfoLabel>Evento</InfoLabel>
                        <EventBadge>{session.pos_event.name}</EventBadge>
                    </InfoRow>
                )}
                {session.observation && (
                    <ObsRow>
                        <InfoIcon><FileText size={15} /></InfoIcon>
                        <ObsText>"{session.observation}"</ObsText>
                    </ObsRow>
                )}
            </InfoCard>

            {/* ─── Balance breakdown ─── */}
            <BalanceGrid>
                <BalanceItem>
                    <BalanceItemLabel>Saldo de abertura</BalanceItemLabel>
                    <BalanceItemValue>{fmt(session.opening_balance)}</BalanceItemValue>
                </BalanceItem>
                <BalanceItem>
                    <BalanceItemLabel>Saldo esperado</BalanceItemLabel>
                    <BalanceItemValue $accent>{fmt(session.expected_balance)}</BalanceItemValue>
                </BalanceItem>
                <BalanceItem>
                    <BalanceItemLabel>Saldo de fechamento</BalanceItemLabel>
                    <BalanceItemValue $bold>{fmt(session.closing_balance)}</BalanceItemValue>
                </BalanceItem>
                <BalanceItem>
                    <BalanceItemLabel>Diferença</BalanceItemLabel>
                    <BalanceItemValue $diff={diff}>
                        {diff > 0 ? <TrendingUp size={14} /> : diff < 0 ? <TrendingDown size={14} /> : <Minus size={14} />}
                        {diff >= 0 ? '+' : ''}{fmt(diff)}
                    </BalanceItemValue>
                </BalanceItem>
            </BalanceGrid>

            {/* ─── Summary stats ─── */}
            {summary && (
                <>
                    <SummaryGrid>
                        <StatCard>
                            <StatIcon $color={colors.success}><DollarSign size={17} /></StatIcon>
                            <StatData>
                                <StatLabel>Total de vendas</StatLabel>
                                <StatValue>{fmt(summary.total_sales)}</StatValue>
                            </StatData>
                        </StatCard>
                        <StatCard>
                            <StatIcon $color={colors.primary}><ShoppingBag size={17} /></StatIcon>
                            <StatData>
                                <StatLabel>Comandas</StatLabel>
                                <StatValue>{summary.total_commands}</StatValue>
                            </StatData>
                        </StatCard>
                        <StatCard>
                            <StatIcon $color={colors.occasionalGreen}><CheckCircle size={17} /></StatIcon>
                            <StatData>
                                <StatLabel>Fechadas</StatLabel>
                                <StatValue>{summary.closed_commands}</StatValue>
                            </StatData>
                        </StatCard>
                        <StatCard>
                            <StatIcon $color={colors.danger}><XCircle size={17} /></StatIcon>
                            <StatData>
                                <StatLabel>Canceladas</StatLabel>
                                <StatValue>{summary.cancelled_commands}</StatValue>
                            </StatData>
                        </StatCard>
                    </SummaryGrid>

                    {/* ─── Payments by method ─── */}
                    {summary.payments_by_method?.length > 0 && (
                        <Section>
                            <SectionTitle>
                                <CreditCard size={15} />
                                Pagamentos por método
                            </SectionTitle>
                            <PaymentsList>
                                {summary.payments_by_method.map((pm, i) => {
                                    const style = getMethodStyle(pm.payment_method);
                                    const total = Number(pm.total) || 0;
                                    const salesTotal = Number(summary.total_sales) || 1;
                                    const pct = Math.round((total / salesTotal) * 100);
                                    return (
                                        <PaymentRow key={i}>
                                            <PaymentDot $color={style.color} />
                                            <PaymentName>{pm.payment_method}</PaymentName>
                                            <PaymentBarWrap>
                                                <PaymentBarFill $color={style.color} $width={pct} />
                                            </PaymentBarWrap>
                                            <PaymentPct>{pct}%</PaymentPct>
                                            <PaymentAmount>{fmt(pm.total)}</PaymentAmount>
                                        </PaymentRow>
                                    );
                                })}
                                {summary.total_tips > 0 && (
                                    <TipsRow>Gorjetas: <strong>{fmt(summary.total_tips)}</strong></TipsRow>
                                )}
                            </PaymentsList>
                        </Section>
                    )}

                    {/* ─── Sales by station ─── */}
                    {summary.sales_by_station?.length > 0 && (
                        <Section>
                            <SectionTitle>
                                <MapPin size={15} />
                                Vendas por estação
                            </SectionTitle>
                            <DataTable>
                                <TableHead>Estação</TableHead>
                                <TableHead $right>Qtd.</TableHead>
                                <TableHead $right>Total</TableHead>
                                {summary.sales_by_station.map((s, i) => (
                                    <React.Fragment key={i}>
                                        <TableCell>{s.station_name ?? 'Sem estação'}</TableCell>
                                        <TableCellNum>{s.quantity}</TableCellNum>
                                        <TableCellNum $bold>{fmt(s.total)}</TableCellNum>
                                    </React.Fragment>
                                ))}
                            </DataTable>
                        </Section>
                    )}

                    {/* ─── Products sold ─── */}
                    {summary.products_sold?.length > 0 && (
                        <Section>
                            <SectionTitle>
                                <Package size={15} />
                                Produtos vendidos
                                {productsPagination.expanded && (
                                    <TotalBadge>{productsPagination.total} no total</TotalBadge>
                                )}
                            </SectionTitle>
                            <DataTable>
                                <TableHead>Produto</TableHead>
                                <TableHead $right>Qtd.</TableHead>
                                <TableHead $right>Total</TableHead>
                                {(productsPagination.expanded ? productsPagination.data : summary.products_sold).map((p, i) => (
                                    <React.Fragment key={i}>
                                        <TableCell>{p.product_name}</TableCell>
                                        <TableCellNum>{p.quantity}</TableCellNum>
                                        <TableCellNum $bold>{fmt(p.total)}</TableCellNum>
                                    </React.Fragment>
                                ))}
                            </DataTable>
                            {!productsPagination.expanded ? (
                                <SeeAllBtn onClick={() => fetchProductsSold(1)} disabled={productsPagination.loading}>
                                    {productsPagination.loading ? 'Carregando...' : 'Ver todos os produtos'}
                                </SeeAllBtn>
                            ) : productsPagination.lastPage > 1 && (
                                <PaginationRow>
                                    <PageBtn onClick={() => fetchProductsSold(productsPagination.page - 1)} disabled={productsPagination.page <= 1 || productsPagination.loading}>
                                        <ChevronLeft size={14} />
                                    </PageBtn>
                                    <PageInfo>{productsPagination.page} / {productsPagination.lastPage}</PageInfo>
                                    <PageBtn onClick={() => fetchProductsSold(productsPagination.page + 1)} disabled={productsPagination.page >= productsPagination.lastPage || productsPagination.loading}>
                                        <ChevronRight size={14} />
                                    </PageBtn>
                                </PaginationRow>
                            )}
                        </Section>
                    )}

                    {/* ─── Tickets sold ─── */}
                    {summary.tickets_sold?.length > 0 && (
                        <Section>
                            <SectionTitle>
                                <Ticket size={15} />
                                Ingressos vendidos
                                {ticketsPagination.expanded && (
                                    <TotalBadge>{ticketsPagination.total} no total</TotalBadge>
                                )}
                            </SectionTitle>
                            <DataTable>
                                <TableHead>Ingresso</TableHead>
                                <TableHead $right>Qtd.</TableHead>
                                <TableHead $right>Total</TableHead>
                                {(ticketsPagination.expanded ? ticketsPagination.data : summary.tickets_sold).map((t, i) => (
                                    <React.Fragment key={i}>
                                        <TableCell>{t.ticket_name}</TableCell>
                                        <TableCellNum>{t.quantity}</TableCellNum>
                                        <TableCellNum $bold>{fmt(t.total)}</TableCellNum>
                                    </React.Fragment>
                                ))}
                            </DataTable>
                            {!ticketsPagination.expanded ? (
                                <SeeAllBtn onClick={() => fetchTicketsSold(1)} disabled={ticketsPagination.loading}>
                                    {ticketsPagination.loading ? 'Carregando...' : 'Ver todos os ingressos'}
                                </SeeAllBtn>
                            ) : ticketsPagination.lastPage > 1 && (
                                <PaginationRow>
                                    <PageBtn onClick={() => fetchTicketsSold(ticketsPagination.page - 1)} disabled={ticketsPagination.page <= 1 || ticketsPagination.loading}>
                                        <ChevronLeft size={14} />
                                    </PageBtn>
                                    <PageInfo>{ticketsPagination.page} / {ticketsPagination.lastPage}</PageInfo>
                                    <PageBtn onClick={() => fetchTicketsSold(ticketsPagination.page + 1)} disabled={ticketsPagination.page >= ticketsPagination.lastPage || ticketsPagination.loading}>
                                        <ChevronRight size={14} />
                                    </PageBtn>
                                </PaginationRow>
                            )}
                        </Section>
                    )}
                </>
            )}
        </Page>
    );
}

/* ═══════════════════════════════════════════════════ */
/*                  STYLED COMPONENTS                  */
/* ═══════════════════════════════════════════════════ */

const rotate = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;

const Page = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
`;

const LoadingWrap = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 80px 0;
`;

const Spinner = styled.div`
    width: 32px; height: 32px;
    border: 3px solid ${colors.neutralDark};
    border-top-color: ${colors.occasionalPurple};
    border-radius: 50%;
    animation: ${rotate} 0.7s linear infinite;
`;

const LoadingLabel = styled.span`font-size: 14px; color: ${colors.nightLight};`;

const ErrorCard = styled.div`
    padding: 20px;
    background: ${colors.danger}10;
    border: 1px solid ${colors.danger}30;
    border-radius: 12px;
    color: ${colors.danger};
    font-size: 14px;
`;

/* ─── Header ─── */
const Header = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
`;

const BackBtn = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border-radius: 10px;
    border: 1.5px solid ${colors.neutralDark};
    background: #fff;
    color: ${colors.night};
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    flex-shrink: 0;
    transition: background 0.15s;
    &:hover { background: ${colors.neutral}; }
`;

const HeaderMeta = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const HeaderDate = styled.span`
    font-size: 18px;
    font-weight: 800;
    color: ${colors.nightDark};
`;

const HeaderTimeRange = styled.span`
    font-size: 13px;
    color: ${colors.nightLight};
`;

/* ─── Info card ─── */
const InfoCard = styled.div`
    background: #fff;
    border: 1px solid ${colors.neutralDark};
    border-radius: 14px;
    padding: 16px 20px;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const InfoRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const InfoIcon = styled.span`
    color: ${colors.nightLight};
    display: flex;
    flex-shrink: 0;
`;

const InfoLabel = styled.span`
    font-size: 13px;
    color: ${colors.nightLight};
    min-width: 90px;
`;

const InfoValue = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const EventBadge = styled.span`
    font-size: 12px;
    padding: 2px 10px;
    border-radius: 8px;
    background: ${colors.occasionalPurple}18;
    color: ${colors.occasionalPurple};
    font-weight: 600;
`;

const ObsRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
`;

const ObsText = styled.span`
    font-size: 13px;
    color: ${colors.night};
    font-style: italic;
`;

/* ─── Balance grid ─── */
const BalanceGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
`;

const BalanceItem = styled.div`
    background: #fff;
    border: 1px solid ${colors.neutralDark};
    border-radius: 12px;
    padding: 14px 16px;
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const BalanceItemLabel = styled.span`
    font-size: 11px;
    font-weight: 600;
    color: ${colors.nightLight};
    text-transform: uppercase;
    letter-spacing: 0.3px;
`;

const BalanceItemValue = styled.span`
    font-size: 16px;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
    display: flex;
    align-items: center;
    gap: 4px;
    color: ${({ $diff, $accent, $bold }) =>
        $diff !== undefined
            ? ($diff >= 0 ? colors.success : colors.danger)
            : $accent
                ? colors.occasionalPurple
                : $bold
                    ? colors.nightDark
                    : colors.night
    };
`;

/* ─── Summary stats ─── */
const SummaryGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
`;

const StatCard = styled.div`
    background: #fff;
    border: 1px solid ${colors.neutralDark};
    border-radius: 12px;
    padding: 14px 16px;
    display: flex;
    align-items: center;
    gap: 12px;
`;

const StatIcon = styled.div`
    width: 36px; height: 36px;
    border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
    background: ${({ $color }) => `${$color}14`};
    color: ${({ $color }) => $color};
`;

const StatData = styled.div`display: flex; flex-direction: column; gap: 2px;`;

const StatLabel = styled.span`
    font-size: 11px;
    font-weight: 600;
    color: ${colors.nightLight};
    text-transform: uppercase;
    letter-spacing: 0.3px;
`;

const StatValue = styled.span`
    font-size: 15px;
    font-weight: 700;
    color: ${colors.nightDark};
    font-variant-numeric: tabular-nums;
`;

/* ─── Sections ─── */
const Section = styled.div`
    background: #fff;
    border: 1px solid ${colors.neutralDark};
    border-radius: 14px;
    padding: 16px 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const SectionTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 13px;
    font-weight: 700;
    color: ${colors.nightDark};
`;

/* ─── Payments ─── */
const PaymentsList = styled.div`display: flex; flex-direction: column; gap: 8px;`;

const PaymentRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const PaymentDot = styled.div`
    width: 8px; height: 8px;
    border-radius: 50%;
    background: ${({ $color }) => $color};
    flex-shrink: 0;
`;

const PaymentName = styled.span`
    font-size: 13px;
    color: ${colors.night};
    min-width: 130px;
`;

const PaymentBarWrap = styled.div`
    flex: 1;
    height: 5px;
    border-radius: 3px;
    background: ${colors.neutralDark};
    overflow: hidden;
`;

const PaymentBarFill = styled.div`
    height: 100%;
    border-radius: 3px;
    background: ${({ $color }) => $color};
    width: ${({ $width }) => Math.max($width, 4)}%;
    transition: width 0.5s ease;
`;

const PaymentPct = styled.span`
    font-size: 11px;
    color: ${colors.nightLight};
    min-width: 30px;
    text-align: right;
`;

const PaymentAmount = styled.span`
    font-size: 14px;
    font-weight: 700;
    color: ${colors.nightDark};
    font-variant-numeric: tabular-nums;
    min-width: 80px;
    text-align: right;
`;

const TipsRow = styled.div`
    font-size: 12px;
    color: ${colors.night};
    padding-top: 8px;
    border-top: 1px solid ${colors.neutralDark};
`;

/* ─── Data table ─── */
const DataTable = styled.div`
    display: grid;
    grid-template-columns: 1fr auto auto;
    column-gap: 16px;
    row-gap: 0;
    align-items: center;
`;

const TableHead = styled.span`
    font-size: 10px;
    font-weight: 700;
    color: ${colors.nightLight};
    text-transform: uppercase;
    letter-spacing: 0.4px;
    padding-bottom: 8px;
    border-bottom: 1px solid ${colors.neutralDark};
    ${({ $right }) => $right && 'text-align: right;'}
`;

const TableCell = styled.span`
    font-size: 13px;
    color: ${colors.night};
    padding: 7px 0;
    border-bottom: 1px solid ${colors.neutral};
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const TableCellNum = styled.span`
    font-size: 13px;
    font-weight: ${({ $bold }) => $bold ? '700' : '600'};
    color: ${colors.nightDark};
    font-variant-numeric: tabular-nums;
    text-align: right;
    padding: 7px 0;
    border-bottom: 1px solid ${colors.neutral};
    white-space: nowrap;
`;

const TotalBadge = styled.span`
    margin-left: auto;
    font-size: 11px;
    font-weight: 600;
    color: ${colors.nightLight};
`;

const SeeAllBtn = styled.button`
    align-self: center;
    margin-top: 4px;
    padding: 6px 16px;
    border-radius: 8px;
    border: 1.5px solid ${colors.neutralDark};
    background: transparent;
    color: ${colors.night};
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s;
    &:hover:not(:disabled) { background: ${colors.neutral}; }
    &:disabled { opacity: 0.5; cursor: default; }
`;

const PaginationRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    margin-top: 4px;
`;

const PageBtn = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px; height: 28px;
    border-radius: 8px;
    border: 1.5px solid ${colors.neutralDark};
    background: #fff;
    color: ${colors.night};
    cursor: pointer;
    transition: background 0.15s;
    &:hover:not(:disabled) { background: ${colors.neutral}; }
    &:disabled { opacity: 0.35; cursor: default; }
`;

const PageInfo = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: ${colors.nightLight};
    min-width: 48px;
    text-align: center;
`;
